// apps/web/src/store/economySlice.ts
// ─────────────────────────────────────────────────────────────────────────
// Redux slice for the gem economy. Holds the balance and a queue of toasts.
//
// Two toast kinds now share the queue:
//   • 'gem'    — standalone "+N gems" (legacy path, e.g. daily login on boot)
//   • 'reward' — combined XP + Gems toast emitted by processRewardEvent
//
// processRewardEvent dispatches awardGems with `silent: true` so the gem
// award doesn't double-toast; it emits ONE 'reward' toast instead.
//
// SYNC PRODUCER
//   Each thunk that actually moves gems now records an economy.* operation via
//   recordEconomy(), so the gem delta replicates to the server ledger. We
//   record only when something was actually credited/spent (credited > 0 / a
//   successful spend) to avoid emitting zero-amount ops the server would reject
//   as invalid_amount. recordEconomy itself no-ops when signed out.
// ─────────────────────────────────────────────────────────────────────────

import {
	createSlice,
	createAsyncThunk,
	type PayloadAction,
} from '@reduxjs/toolkit';
import {
	awardGems as libAwardGems,
	spendGems as libSpendGems,
	getBalance,
	getRecentTransactions,
	claimDailyLogin as libClaimDailyLogin,
	awardStreakMilestone as libAwardStreakMilestone,
} from '../lib/economy';
import { recordEconomy } from '../sync/webSync';
import { loadStreak, addStreakFreeze } from '../lib/srs';
import { STREAK_FREEZE_COST } from '@nisk/core/economy';
import { MAX_STREAK_FREEZES } from '@nisk/core/srs';
import type {
	EarnEventType,
	SpendEventType,
	GemBalance,
	GemTransaction,
} from '@nisk/core/economy';

// ── Toast types ──────────────────────────────────────────────────────────
export interface GemToastItem {
	kind: 'gem';
	id: string;
	amount: number;
	eventType: EarnEventType | SpendEventType;
	reason?: string;
	/** @deprecated The daily gem cap was removed; this is never set anymore.
	 *  Kept optional so legacy render paths still typecheck. */
	capped?: boolean;
}

export interface RewardToastItem {
	kind: 'reward';
	id: string;
	headline: string;
	xp: number;
	gems: number;
	leveledUp?: boolean;
	levelAfter?: number;
}

export type ToastItem = GemToastItem | RewardToastItem;

// ── Slice state ──────────────────────────────────────────────────────────
export interface EconomyState {
	balance: GemBalance | null;
	recentTransactions: GemTransaction[];
	toastQueue: ToastItem[];
	initialized: boolean;
}

const initialState: EconomyState = {
	balance: null,
	recentTransactions: [],
	toastQueue: [],
	initialized: false,
};

// ── Reason labels (for standalone gem toasts) ────────────────────────────
// Copy rule: no em-dashes or en-dashes in user-facing strings.
const REASON_LABELS: Partial<Record<EarnEventType, string>> = {
	daily_login: 'Daily Login',
	streak_milestone: 'Streak Milestone',
	level_up: 'Level Up!',
	quiz_pass_first_try: 'Quiz Passed on the First Try!',
	quiz_pass_subsequent: 'Quiz Passed',
	quiz_aced: 'Perfect Score!',
	module_complete: 'Module Complete!',
	impact_node_reward: 'Bonus Decision Complete',
	lesson_audio_complete: 'Lesson Audio Complete',
	playlist_complete: 'Playlist Complete!',
	final_test_pass: 'Final Test Passed!',
	discovery_quiz_complete: 'Discovery Quiz Complete!',
	daily_review_cleared: 'Daily Review Cleared!',
	lesson_applied: 'Applied to Your Numbers',
	scenario_first_saved: 'First Scenario Saved!',
	market_replay_first: 'Market Replay Complete!',
	school_exhibit_complete: 'Worldview Explored!',
	first_install: 'Welcome Bonus',
	iap_purchase: 'Purchase',
};

function nextToastId(): string {
	return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

// ── Thunks ───────────────────────────────────────────────────────────────

/** Load balance + recent transactions from storage. Call once on app boot. */
export const initializeEconomy = createAsyncThunk(
	'economy/initialize',
	async () => {
		return {
			balance: getBalance(),
			recentTransactions: getRecentTransactions(),
		};
	},
);

/**
 * Award gems for an event. Pass `silent: true` to suppress the standalone gem
 * toast (used by processRewardEvent, which emits a combined reward toast).
 */
export const awardGems = createAsyncThunk(
	'economy/award',
	async (
		args: {
			eventType: EarnEventType;
			entityId?: string;
			streakDays?: number;
			silent?: boolean;
		},
		{ dispatch },
	) => {
		const result = libAwardGems(args.eventType, args.entityId, {
			streakDays: args.streakDays,
		});

		// Replicate the gem credit to the server ledger. Only when something
		// was actually credited (a zero award would be rejected server-side).
		if (result.credited > 0) {
			recordEconomy({
				type: 'economy.earn',
				entityId: args.entityId ?? args.eventType,
				amount: result.credited,
				currency: 'gems',
			});
		}

		if (!args.silent && result.credited > 0) {
			dispatch(
				economySlice.actions.enqueueGemToast({
					kind: 'gem',
					id: nextToastId(),
					amount: result.credited,
					eventType: args.eventType,
					reason: REASON_LABELS[args.eventType],
				}),
			);
		}

		return {
			balance: result.balance,
			credited: result.credited,
			recentTransactions: getRecentTransactions(),
		};
	},
);

/** Claim the daily login reward if not already claimed today. */
export const claimDailyLogin = createAsyncThunk(
	'economy/claimDailyLogin',
	async (_, { dispatch }) => {
		const result = libClaimDailyLogin();
		if (!result.alreadyClaimed && result.credited > 0) {
			recordEconomy({
				type: 'economy.earn',
				entityId: 'daily_login',
				amount: result.credited,
				currency: 'gems',
			});
			dispatch(
				economySlice.actions.enqueueGemToast({
					kind: 'gem',
					id: nextToastId(),
					amount: result.credited,
					eventType: 'daily_login',
					reason: REASON_LABELS.daily_login,
				}),
			);
		}
		return {
			balance: result.balance,
			credited: result.credited,
			alreadyClaimed: result.alreadyClaimed,
			recentTransactions: getRecentTransactions(),
		};
	},
);

/** Award a streak milestone (3, 7, 14, 30, 60, 100, 365 day thresholds). */
export const awardStreakMilestone = createAsyncThunk(
	'economy/awardStreakMilestone',
	async (streakDays: number, { dispatch }) => {
		const result = libAwardStreakMilestone(streakDays);
		if (result.credited > 0) {
			recordEconomy({
				type: 'economy.earn',
				entityId: `streak_${streakDays}`,
				amount: result.credited,
				currency: 'gems',
			});
			dispatch(
				economySlice.actions.enqueueGemToast({
					kind: 'gem',
					id: nextToastId(),
					amount: result.credited,
					eventType: 'streak_milestone',
					reason: `${streakDays}-Day Streak!`,
				}),
			);
		}
		return {
			balance: result.balance,
			credited: result.credited,
			recentTransactions: getRecentTransactions(),
		};
	},
);

/** Spend gems on a cosmetic item. Returns ok=false if insufficient balance. */
export const spendGems = createAsyncThunk(
	'economy/spend',
	async (args: {
		eventType: SpendEventType;
		amount: number;
		entityId?: string;
		metadata?: Record<string, unknown>;
	}) => {
		const result = libSpendGems(
			args.eventType,
			args.amount,
			args.entityId,
			args.metadata,
		);

		// Replicate only a successful spend; a rejected spend changed nothing.
		if (result.ok && args.amount > 0) {
			recordEconomy({
				type: 'economy.spend',
				entityId: args.entityId ?? args.eventType,
				amount: args.amount,
				currency: 'gems',
			});
		}

		return {
			ok: result.ok,
			reason: result.reason,
			balance: result.balance,
			recentTransactions: getRecentTransactions(),
		};
	},
);

/**
 * Buy a Streak Freeze — the ONLY thing gems buy at launch. Guards the
 * MAX_STREAK_FREEZES cap, spends through the normal ledger (replicated via
 * recordEconomy like every other spend), then increments the freeze
 * inventory in the streak record (lib/srs owns that write; consumption is
 * automatic inside core applyStudyToday). A calm toast confirms —
 * protective framing, never a siren.
 */
export const purchaseStreakFreeze = createAsyncThunk(
	'economy/purchaseStreakFreeze',
	async (_, { dispatch }) => {
		const streak = loadStreak();
		if (streak.freezesOwned >= MAX_STREAK_FREEZES) {
			return {
				ok: false,
				reason: 'max_owned' as string | undefined,
				balance: getBalance(),
				recentTransactions: getRecentTransactions(),
				freezesOwned: streak.freezesOwned,
			};
		}

		const result = libSpendGems(
			'streak_freeze',
			STREAK_FREEZE_COST,
			'streak-freeze',
		);
		if (!result.ok) {
			return {
				ok: false,
				reason: result.reason,
				balance: result.balance,
				recentTransactions: getRecentTransactions(),
				freezesOwned: streak.freezesOwned,
			};
		}

		recordEconomy({
			type: 'economy.spend',
			entityId: 'streak-freeze',
			amount: STREAK_FREEZE_COST,
			currency: 'gems',
		});

		const next = addStreakFreeze();
		dispatch(
			economySlice.actions.enqueueRewardToast({
				headline: `🧊 Streak Freeze added. ${next.freezesOwned} of ${MAX_STREAK_FREEZES} ready`,
				xp: 0,
				gems: 0,
			}),
		);

		return {
			ok: true,
			reason: undefined as string | undefined,
			balance: result.balance,
			recentTransactions: getRecentTransactions(),
			freezesOwned: next.freezesOwned,
		};
	},
);

// ── Slice ────────────────────────────────────────────────────────────────
const economySlice = createSlice({
	name: 'economy',
	initialState,
	reducers: {
		enqueueGemToast(state, action: PayloadAction<GemToastItem>) {
			state.toastQueue.push(action.payload);
		},
		/** Combined XP + Gems toast (from processRewardEvent). */
		enqueueRewardToast(
			state,
			action: PayloadAction<Omit<RewardToastItem, 'kind' | 'id'>>,
		) {
			state.toastQueue.push({
				kind: 'reward',
				id: nextToastId(),
				...action.payload,
			});
		},
		dismissToast(state, action: PayloadAction<string>) {
			state.toastQueue = state.toastQueue.filter(
				(t) => t.id !== action.payload,
			);
		},
		/**
		 * Snap the reactive balance to a server-authoritative value. Used by
		 * multi-device pull reconciliation — the caller has already written the
		 * value through `reconcileBalanceFromServer` (lib/economy), so this only
		 * updates the live slice. State-only: no toast, no transaction.
		 */
		reconcileBalance(state, action: PayloadAction<GemBalance>) {
			state.balance = action.payload;
		},
		clearToasts(state) {
			state.toastQueue = [];
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(initializeEconomy.fulfilled, (state, action) => {
				state.balance = action.payload.balance;
				state.recentTransactions = action.payload.recentTransactions;
				state.initialized = true;
			})
			.addCase(awardGems.fulfilled, (state, action) => {
				state.balance = action.payload.balance;
				state.recentTransactions = action.payload.recentTransactions;
			})
			.addCase(claimDailyLogin.fulfilled, (state, action) => {
				state.balance = action.payload.balance;
				state.recentTransactions = action.payload.recentTransactions;
			})
			.addCase(awardStreakMilestone.fulfilled, (state, action) => {
				state.balance = action.payload.balance;
				state.recentTransactions = action.payload.recentTransactions;
			})
			.addCase(spendGems.fulfilled, (state, action) => {
				state.balance = action.payload.balance;
				state.recentTransactions = action.payload.recentTransactions;
			})
			.addCase(purchaseStreakFreeze.fulfilled, (state, action) => {
				state.balance = action.payload.balance;
				state.recentTransactions = action.payload.recentTransactions;
			});
	},
});

export const {
	enqueueGemToast,
	enqueueRewardToast,
	dismissToast,
	reconcileBalance,
	clearToasts,
} = economySlice.actions;
export default economySlice.reducer;

// ── Selectors ────────────────────────────────────────────────────────────
export type WithEconomy = { economy: EconomyState };

export const selectGemBalance = (s: WithEconomy) =>
	s.economy.balance?.total ?? 0;
export const selectLifetimeEarned = (s: WithEconomy) =>
	s.economy.balance?.lifetimeEarned ?? 0;
export const selectLifetimeSpent = (s: WithEconomy) =>
	s.economy.balance?.lifetimeSpent ?? 0;
export const selectToastQueue = (s: WithEconomy) => s.economy.toastQueue;
